<template>
  <div :class="`w-full relative ${containerClasses}`">
    <input
      :id="inputId"
      ref="autocompleteInput"
      v-model="inputValue"
      data-input-autocomplete
      :qaid="`input-autocomplete ${inputQaid}`"
      :placeholder="inputPlaceholder"
      type="text"
      :class="inputClasses"
      :name="inputName"
      :required="inputRequired"
      :maxlength="inputMaxLength"
      class="mb-0"
      autocomplete="off"
      :disabled="disabled"
      @keyup.enter="emitEnterSearchEvent"
      @blur="hideDropdown"
      @focus="showDropdown"
    />
    <ul v-show="isShowingSuggestionList" class="absolute top-full w-full z-30" qaid="input-autocomplete-list">
      <li
        v-for="(suggestion, index) in suggestionList"
        :key="suggestion.id"
        :qaid="`${suggestion.qaid ? suggestion.qaid : 'suggestion'}-${index}`"
        class="border border-t-0 border-gray-20 bg-white cursor-pointer first:border-t"
      >
        <button
          class="w-full py-1 pr-1 pl-2 leading-normal hover:bg-blue-100 hover:text-white hover:border-blue-100"
          type="button"
          @click="selectSuggestion(suggestion, true)"
          @mousedown.prevent="selectSuggestion(suggestion, true)"
        >
          {{ suggestion.value }}
        </button>
      </li>
      <li
        v-if="isAutoLocationSupported && !isGeolocationQueryScheduled"
        class="auto-location flex justify-between border border-t-0 border-gray-20 bg-white cursor-pointer first:border-t"
        qaid="store-locator__search__auto-location"
      >
        <button
          :class="{processing: isAutoDetectingLocation}"
          class="w-full py-1 pr-1 pl-2 leading-normal hover:bg-blue-100 hover:text-white hover:border-blue-100"
          type="button"
        >
          {{ $t('storeFinder.v2.useLocation') }}
        </button>
      </li>
    </ul>
  </div>
</template>
<script>
import isEmpty from 'lodash/isEmpty';

export default {
  props: {
    inputClasses: {
      type: String,
      default: '',
    },
    containerClasses: {
      type: String,
      default: '',
    },
    inputId: {
      type: String,
      default: '',
    },
    inputQaid: {
      type: String,
      default: '',
    },
    inputName: {
      type: String,
      default: 'address',
    },
    inputRequired: {
      type: String,
      default: null,
    },
    inputPlaceholder: {
      type: String,
      default: '',
    },
    inputMaxLength: {
      type: Number,
      default: 150,
    },
    suggestionList: {
      type: Array,
      default: () => [],
    },
    initialValue: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    forceSuggestionSelection: {
      type: Boolean,
      default: false,
    },
    nearLocationValue: {
      type: String,
      default: '',
    },
    isAutoLocationSupported: {
      type: Boolean,
      default: false,
    },
    isAutoDetectingLocation: {
      type: Boolean,
      default: false,
    },
    queryGeolocation: {
      type: Function,
      default: null,
    },
    isGeolocationQueryScheduled: {
      type: Boolean,
      default: false,
    },
    isAddressLineInput: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: '',
      hasSuggestionDropdownOpen: false,
      isSelecting: false,
      selectedSuggestion: undefined,
    };
  },

  computed: {
    isShowingSuggestionList() {
      return this.suggestionList && this.hasSuggestionDropdownOpen;
    },
  },
  watch: {
    initialValue(newValue) {
      this.inputValue = newValue;
    },
    inputValue(newValue) {
      if (this.isSelecting) return;
      this.$emit('input-updated', {inputValue: newValue, data: {}, isSearching: false});
    },
  },
  created() {
    if (this.initialValue) {
      const initialSuggestion = {
        value: this.initialValue,
        data: {},
      };
      this.selectedSuggestion = initialSuggestion;
      this.selectSuggestion(this.selectedSuggestion);
    }
  },
  methods: {
    emitSearchEvent(suggestion) {
      this.$refs?.autocompleteInput?.blur();
      this.$emit('input-updated', {
        inputValue: suggestion.value,
        data: suggestion.data,
        isSearching: true,
        placeNameIdentifier: suggestion.data.place_name,
        selectedOnList: suggestion.selectedOnList,
      });
    },
    emitEnterSearchEvent() {
      this.$refs?.autocompleteInput?.blur();
      this.$emit('input-updated', {inputValue: this.inputValue, data: {}, isSearching: true});
    },
    selectSuggestion(suggestion, selectedOnList = false) {
      this.isSelecting = true;
      if (this.isAddressLineInput) {
        if (isEmpty(suggestion.data)) {
          this.inputValue = suggestion.value;
        } else {
          this.inputValue = suggestion.data.address
            ? `${suggestion.data.address} ${suggestion.data.text}`
            : suggestion.data.text;
        }
      } else {
        this.inputValue = suggestion.value;
      }
      this.selectedSuggestion = {
        value: this.inputValue,
        data: suggestion.data,
        selectedOnList,
      };
      this.emitSearchEvent(this.selectedSuggestion);
      this.isSelecting = false;
      this.hideDropdown();
    },
    hideDropdown() {
      const DELAY_FOR_SUGGESTION_CLICK = 200;
      setTimeout(() => {
        this.hasSuggestionDropdownOpen = false;
        if (this.forceSuggestionSelection) {
          this.enforceSuggestionListSelection();
        }
      }, DELAY_FOR_SUGGESTION_CLICK);
    },

    setNearValue() {
      this.inputValue = this.nearLocationValue;
      this.queryGeolocation();
    },

    showDropdown() {
      this.hasSuggestionDropdownOpen = true;
    },

    async enforceSuggestionListSelection() {
      await this.$nextTick();
      const isIssueFromSuggestionList = this.suggestionList.findIndex((issue) => issue.value === this.inputValue) > -1;
      if (isIssueFromSuggestionList) {
        this.$emit('valid-selection', this.inputValue);
      } else {
        this.inputValue = null;
      }
    },
  },
};
</script>
